import { useState, useRef, useEffect, useCallback } from 'react';

interface SmsConsentDisclosureProps {
  script: string;
  intro?: string;
}

export function SmsConsentDisclosure({ script, intro }: SmsConsentDisclosureProps) {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const positionTooltip = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipWidth = 300;
    const centered = rect.left + rect.width / 2 - tooltipWidth / 2;
    setAlignRight(centered + tooltipWidth > window.innerWidth - 8);
  }, []);

  useEffect(() => {
    if (!open) return;
    positionTooltip();
    const handleClick = (e: MouseEvent) => {
      if (
        tooltipRef.current && !tooltipRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, positionTooltip]);

  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: '4px', verticalAlign: 'middle', marginTop: '-2px' }}>
      <button
        ref={triggerRef}
        type="button"
        aria-label="View full SMS disclosure"
        onClick={() => setOpen(!open)}
        style={{
          background: 'none',
          border: '1px solid var(--border-color)',
          borderRadius: '50%',
          width: '16px',
          height: '16px',
          fontSize: '11px',
          lineHeight: '14px',
          cursor: 'pointer',
          color: 'var(--text-muted)',
          padding: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ?
      </button>
      {open && (
        <div
          ref={tooltipRef}
          role="tooltip"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            ...(alignRight
              ? { right: 0 }
              : { left: '50%', transform: 'translateX(-50%)' }),
            width: '300px',
            background: '#1f2937',
            color: '#f3f4f6',
            fontSize: '0.8rem',
            lineHeight: 1.4,
            padding: '10px 12px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            zIndex: 1000,
          }}
        >
          {intro && (
            <div style={{ fontWeight: 600, marginBottom: '6px', fontSize: '0.75rem', opacity: 0.8 }}>
              {intro}
            </div>
          )}
          {script}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              ...(alignRight
                ? { right: '4px' }
                : { left: '50%', transform: 'translateX(-50%)' }),
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid #1f2937',
            }}
          />
        </div>
      )}
    </span>
  );
}
